import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface DashboardFilterState {
  ReportFilterType: number;
  QuarterlyYearType: number;
  HalfYearType: number;
  Month: number;
  FinancialYearRef: number;
  IncentiveRole: number;
  FromDate: string;
  ToDate: string;

  AnemiaMuktaBharatType: number;
  ContraceptivePillType: number;
  ContraceptivePillTypeName?: string;
  AnemiaMuktaBharatTabletclickedItem: string;
  IncentiveType: number;
  Title: string;

  drillDownType: number;
  drillDownSubType: number;

  state_ids: number[];
  zone_ids: number[];
  district_ids: number[];
  block_ids: number[];
  phc_ids: number[];
  af_ids: number[];
  sc_ids: number[];
  village_ids: number[];
  asha_ids: number[];
  DCMPostRefs: number[];
  bcm_post_ids: number[];
  asha_employee_record_ids: number[];
  af_employee_record_ids: number[];
  bcm_employee_record_ids: number[];
}

@Injectable({
  providedIn: 'root'
})
export class DashboardFilterStateStorageService {

  private readonly STORAGE_KEY = 'dashboard_filter_state';

  private readonly defaultState: DashboardFilterState = {
    ReportFilterType: 0,
    QuarterlyYearType: 0,
    HalfYearType: 0,
    Month: 0,
    FinancialYearRef: 0,
    IncentiveRole: 0,
    FromDate: '',
    ToDate: '',

    AnemiaMuktaBharatType: 0,
    ContraceptivePillType: 0,
    ContraceptivePillTypeName: '',
    AnemiaMuktaBharatTabletclickedItem: '',
    IncentiveType: 0,
    Title: '',

    drillDownType: 0,
    drillDownSubType: 0,

    state_ids: [],
    zone_ids: [],
    district_ids: [],
    block_ids: [],
    phc_ids: [],
    af_ids: [],
    sc_ids: [],
    village_ids: [],
    asha_ids: [],
    DCMPostRefs: [],
    bcm_post_ids: [],
    asha_employee_record_ids: [],
    af_employee_record_ids: [],
    bcm_employee_record_ids: []
  };

  private filterStateSubject =
    new BehaviorSubject<DashboardFilterState>(
      this.loadFromSessionStorage()
    );

  filterState$ =
    this.filterStateSubject.asObservable();

  // ============================================================
  // GET CURRENT STATE
  // ============================================================

  getFilterState(): DashboardFilterState {
    return this.cloneState(
      this.filterStateSubject.value
    );
  }

  // ============================================================
  // SET COMPLETE / PARTIAL STATE
  // ============================================================

  setFilterState(
    state: Partial<DashboardFilterState>
  ): void {

    const current =
      this.filterStateSubject.value;

    const updated: DashboardFilterState = {
      ...current,
      ...state,

      state_ids: [
        ...(state.state_ids ?? current.state_ids)
      ],

      zone_ids: [
        ...(state.zone_ids ?? current.zone_ids)
      ],

      district_ids: [
        ...(state.district_ids ?? current.district_ids)
      ],

      block_ids: [
        ...(state.block_ids ?? current.block_ids)
      ],

      phc_ids: [
        ...(state.phc_ids ?? current.phc_ids)
      ],

      af_ids: [
        ...(state.af_ids ?? current.af_ids)
      ],

      sc_ids: [
        ...(state.sc_ids ?? current.sc_ids)
      ],

      village_ids: [
        ...(state.village_ids ?? current.village_ids)
      ],

      asha_ids: [
        ...(state.asha_ids ?? current.asha_ids)
      ],

      DCMPostRefs: [
        ...(state.DCMPostRefs ?? current.DCMPostRefs)
      ],

      bcm_post_ids: [
        ...(state.bcm_post_ids ?? current.bcm_post_ids)
      ],

      asha_employee_record_ids: [
        ...(state.asha_employee_record_ids ??
          current.asha_employee_record_ids)
      ],

      af_employee_record_ids: [
        ...(state.af_employee_record_ids ??
          current.af_employee_record_ids)
      ],

      bcm_employee_record_ids: [
        ...(state.bcm_employee_record_ids ??
          current.bcm_employee_record_ids)
      ]
    };

    this.filterStateSubject.next(
      this.cloneState(updated)
    );

    this.saveToSessionStorage(updated);
  }

  // ============================================================
  // UPDATE SINGLE FIELD
  // ============================================================

  updateField<K extends keyof DashboardFilterState>(
    key: K,
    value: DashboardFilterState[K]
  ): void {

    const current =
      this.filterStateSubject.value;

    const updated: DashboardFilterState = {
      ...current,
      [key]: value
    };

    this.filterStateSubject.next(
      this.cloneState(updated)
    );

    this.saveToSessionStorage(updated);
  }

  // ============================================================
  // CLEAR
  // ============================================================

  clearFilterState(): void {

    const emptyState =
      this.cloneState(this.defaultState);

    this.filterStateSubject.next(
      emptyState
    );

    sessionStorage.removeItem(
      this.STORAGE_KEY
    );
  }

  // ============================================================
  // SESSION STORAGE
  // ============================================================

  private saveToSessionStorage(
    state: DashboardFilterState
  ): void {

    try {

      sessionStorage.setItem(
        this.STORAGE_KEY,
        JSON.stringify(state)
      );

    } catch (error) {

      console.error(
        'Unable to save dashboard filter state:',
        error
      );
    }
  }

  private loadFromSessionStorage():
    DashboardFilterState {

    try {

      const stored =
        sessionStorage.getItem(
          this.STORAGE_KEY
        );

      if (!stored) {
        return this.cloneState(
          this.defaultState
        );
      }

      const parsed =
        JSON.parse(stored);

      return {
        ...this.cloneState(
          this.defaultState
        ),
        ...parsed,

        state_ids: [
          ...(parsed.state_ids ?? [])
        ],

        zone_ids: [
          ...(parsed.zone_ids ?? [])
        ],

        district_ids: [
          ...(parsed.district_ids ?? [])
        ],

        block_ids: [
          ...(parsed.block_ids ?? [])
        ],

        phc_ids: [
          ...(parsed.phc_ids ?? [])
        ],

        af_ids: [
          ...(parsed.af_ids ?? [])
        ],

        sc_ids: [
          ...(parsed.sc_ids ?? [])
        ],

        village_ids: [
          ...(parsed.village_ids ?? [])
        ],

        asha_ids: [
          ...(parsed.asha_ids ?? [])
        ],

        DCMPostRefs: [
          ...(parsed.DCMPostRefs ?? [])
        ],

        bcm_post_ids: [
          ...(parsed.bcm_post_ids ?? [])
        ],

        asha_employee_record_ids: [
          ...(parsed.asha_employee_record_ids ?? [])
        ],

        af_employee_record_ids: [
          ...(parsed.af_employee_record_ids ?? [])
        ],

        bcm_employee_record_ids: [
          ...(parsed.bcm_employee_record_ids ?? [])
        ]
      };

    } catch (error) {

      console.error(
        'Unable to restore dashboard filter state:',
        error
      );

      return this.cloneState(
        this.defaultState
      );
    }
  }

  // ============================================================
  // CLONE
  // ============================================================

  private cloneState(
    state: DashboardFilterState
  ): DashboardFilterState {

    return {
      ...state,

      state_ids: [...state.state_ids],
      zone_ids: [...state.zone_ids],
      district_ids: [...state.district_ids],
      block_ids: [...state.block_ids],
      phc_ids: [...state.phc_ids],
      af_ids: [...state.af_ids],
      sc_ids: [...state.sc_ids],
      village_ids: [...state.village_ids],
      asha_ids: [...state.asha_ids],

      DCMPostRefs: [...state.DCMPostRefs],
      bcm_post_ids: [...state.bcm_post_ids],
      asha_employee_record_ids: [
        ...state.asha_employee_record_ids
      ],
      af_employee_record_ids: [
        ...state.af_employee_record_ids
      ],
      bcm_employee_record_ids: [
        ...state.bcm_employee_record_ids
      ]
    };
  }

  setDrillDownContext(drillDownType: number, drillDownSubType: number = 0): void {
    this.setFilterState({ drillDownType, drillDownSubType });
  }

  getDrillDownContext(): { drillDownType: number; drillDownSubType: number } {
    const state = this.getFilterState();
    return {
      drillDownType: state.drillDownType,
      drillDownSubType: state.drillDownSubType,
    };
  }

  clearDrillDownContext(): void {
    this.setFilterState({ drillDownType: 0, drillDownSubType: 0 });
  }
}